import {
  Search,
  Plus,
  BookOpen,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip } from '@/components/ui/tooltip'
import type { LibraryCategory } from '@/types/api'

export const TOOLTIP_STYLE = {
  color: 'black',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  padding: '6px 12px',
  fontSize: 13,
}

interface LibrarySidebarProps {
  categories: LibraryCategory[]
  activeCategoryId: string | null
  searchTerm: string
  onSearchChange: (value: string) => void
  pageIndex: number
  totalPages: number
  onPageChange: (page: number) => void
  onSelectCategory: (id: string) => void
  onAddCategory: () => void
  onRenameCategory: (category: LibraryCategory) => void
  onDeleteCategory: (category: LibraryCategory) => void
}

export function LibrarySidebar({
  categories,
  activeCategoryId,
  searchTerm,
  onSearchChange,
  pageIndex,
  totalPages,
  onPageChange,
  onSelectCategory,
  onAddCategory,
  onRenameCategory,
  onDeleteCategory,
}: LibrarySidebarProps) {
  return (
    <div className="flex h-full w-full shrink-0 flex-col rounded-xl border border-gray-300 bg-white p-4 lg:w-[350px]">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-xl font-bold">Danh sách thư viện</h2>
        <Button
          variant="outline"
          size="icon"
          onClick={onAddCategory}
          className="flex h-[35px] w-[35px] !border-[#CE7A58] !text-[#CE7A58] hover:!bg-[#CE7A58]/10"
        >
          <Plus size={20} />
        </Button>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Nhập tên thư viện..."
          prefix={<Search size={16} className="mr-1.5" />}
          value={searchTerm}
          onChange={(e: { target: { value: string } }) =>
            onSearchChange(e.target.value)
          }
        />
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto pr-1">
        {categories.map((category) => {
          const isActive = category.id === activeCategoryId
          return (
            <div
              key={category.id}
              role="button"
              tabIndex={0}
              aria-pressed={isActive}
              className={`group flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                isActive
                  ? 'bg-[#CE7A58]/10 text-[#CE7A58] font-medium'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={() => onSelectCategory(category.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelectCategory(category.id)
                }
              }}
            >
              <div className="flex items-center gap-3 overflow-hidden">
                <BookOpen
                  size={18}
                  className={`shrink-0 ${isActive ? 'text-black' : 'text-gray-500 group-hover:text-black'}`}
                />

                <Tooltip
                  title={category.name}
                  placement="topLeft"
                  color="#fff"
                  styles={{ container: TOOLTIP_STYLE }}
                >
                  <span className="truncate">{category.name}</span>
                </Tooltip>
              </div>

              <div
                role="presentation"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                className="shrink-0 pl-2"
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal size={16} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      onClick={() => onRenameCategory(category)}
                    >
                      Đổi tên
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => onDeleteCategory(category)}
                    >
                      Xóa
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )
        })}
        {categories.length === 0 && (
          <div className="text-center text-sm text-gray-500 mt-4">
            Không tìm thấy thư viện nào.
          </div>
        )}
      </div>

      {/* Phân trang Sidebar */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between border-t pt-3">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            disabled={pageIndex === 0}
            onClick={() => onPageChange(pageIndex - 1)}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="text-xs font-medium text-gray-500">
            {pageIndex + 1} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            disabled={pageIndex >= totalPages - 1}
            onClick={() => onPageChange(pageIndex + 1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
    </div>
  )
}
